import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiBookOpen,
  FiBriefcase,
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
  FiFileText,
  FiHome,
  FiLayers,
  FiMail,
  FiPieChart,
  FiSettings,
  FiShield,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { SidebarContext } from './SidebarContext';
import { getStoredCompanyBranding, resolveLogoUrl } from '../utils/companyTheme';

const Sidebar = () => {
  const { isSidebarOpen, toggleSidebar } = useContext(SidebarContext);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [branding, setBranding] = useState(getStoredCompanyBranding);
  const location = useLocation();

  useEffect(() => {
    setUserRole(localStorage.getItem('role'));
  }, []);

  useEffect(() => {
    const syncBranding = () => setBranding(getStoredCompanyBranding());
    window.addEventListener('company-branding-updated', syncBranding);
    window.addEventListener('storage', syncBranding);
    return () => {
      window.removeEventListener('company-branding-updated', syncBranding);
      window.removeEventListener('storage', syncBranding);
    };
  }, []);

  const isPrivileged = userRole === 'admin' || userRole === 'company_owner';

  const getIcon = (iconName) => {
    const icons = {
      DB: <FiHome size={18} />,
      TX: <FiBookOpen size={18} />,
      RP: <FiPieChart size={18} />,
      AR: <FiFileText size={18} />,
      ED: <FiEdit2 size={18} />,
      RQ: <FiMail size={18} />,
      CM: <FiBriefcase size={18} />,
      UM: <FiUsers size={18} />,
      AT: <FiLayers size={18} />,
      ST: <FiSettings size={18} />,
    };
    return icons[iconName] || <FiShield size={18} />;
  };

  const menuGroups = [
    { title: 'Dashboard', icon: 'DB', path: '/dashboard', singleItem: true },
    {
      title: 'Daily Txn Book',
      icon: 'TX',
      items: [
        { path: '/transaction-form', label: 'Transaction Form' },
        { path: '/transaction_Search', label: 'Search Form' },
        ...(['worker'].includes(userRole) ? [{ path: '/transaction-report', label: 'Transaction Report' }] : []),
        ...(['worker'].includes(userRole) ? [{ path: '/Return', label: userRole === 'admin' ? 'Return To Company' : 'Return Management Report' }] : []),
        ...(isPrivileged ? [{ path: '/my-transactions', label: 'My Transactions' }] : []),
      ],
    },
    ...(userRole === 'worker'
      ? [{
          title: 'Reports',
          icon: 'RP',
          items: [
            { path: '/SpecificWorkerReport', label: 'Request Report' },
            { path: '/ReportReturn', label: 'Return Report' },
          ],
        }]
      : []),
    ...(isPrivileged
      ? [
          {
            title: userRole === 'company_owner' ? 'Company Reports' : 'All Reports',
            icon: 'AR',
            items: [
              { path: '/detailedreport', label: 'Detailed Record' },
              { path: '/ledger-reports', label: 'Ledger Reports' },
              { path: '/my-transactions', label: userRole === 'company_owner' ? 'My Transactions' : 'My Transaction Report' },
              { path: '/usertransaction', label: 'See User Details' },
              ...(userRole === 'company_owner' ? [{ path: '/admin-transaction-details', label: 'See Admin Details' }] : []),
              {
                path: '/AdminReturn',
                label: 'Worker Return Requests',
              },
              ...(userRole === 'company_owner' ? [{ path: '/company-owner-returns', label: 'Admin Return Requests' }] : []),
            ],
            adminOnly: true,
          },
          {
            title: 'Edit Management',
            icon: 'ED',
            items: [{ path: '/Edit', label: 'Edit Transaction' }],
            adminOnly: true,
          },
          {
            title: 'Requests',
            icon: 'RQ',
            items: [
              { path: '/RequestReport', label: 'Worker Request Report' },
              { path: '/worker-request-history', label: 'Worker Request History' },
              ...(userRole === 'company_owner' ? [{ path: '/admin-request-report', label: 'Admin Fund Requests' }] : []),
              { path: '/admin-request-history', label: 'Admin Request History' },
            ],
            adminOnly: true,
          },
          {
            title: 'Settings',
            icon: 'ST',
            items: [
              { path: '/threshold-management', label: 'Threshold Settings' },
              ...(userRole === 'company_owner' ? [{ path: '/transaction-photo-settings', label: 'Photo Settings' }] : []),
            ],
            adminOnly: true,
          },
        ]
      : []),
    ...(userRole === 'company_owner'
      ? [{
          title: 'Company Management',
          icon: 'CM',
          items: [
            { path: '/admin-details', label: 'Admin Details' },
            { path: '/company-summary', label: 'User Summary' },
            { path: '/company-plan-profile', label: 'Plan Profile' },
          ],
          adminOnly: true,
        }]
      : []),
    {
      title: 'User Management',
      icon: 'UM',
      items: [
        { path: '/createuser', label: 'Create User' },
        { path: '/UserReport', label: 'User Report' },
      ],
      adminOnly: true,
    },
    {
      title: 'Attributes',
      icon: 'AT',
      items: [
        { path: '/addattribute', label: 'Add Attribute' },
        { path: '/AddSite', label: 'Add Site' },
        { path: '/projects', label: 'Projects' },
        { path: '/attributeReport', label: 'Attribute Report' },
      ],
      adminOnly: true,
    },
  ].filter((group) => !group.adminOnly || isPrivileged);

  const toggleDropdown = (index) => setActiveDropdown(activeDropdown === index ? null : index);

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      toggleSidebar();
    }
  };
  const companyLogo = resolveLogoUrl(branding.logo_path);
  const companyName = branding.company_name || localStorage.getItem('company_name') || 'Transaction Hub';

  return (
    <>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-950/45 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      <aside
        className={`fixed left-0 top-16 z-40 h-[calc(100vh-4rem)] w-72 overflow-y-auto border-r border-slate-200 bg-white text-slate-700 shadow-2xl transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:w-72 lg:translate-x-0`}
      >
        <div className="relative flex items-center justify-between border-b border-slate-200 bg-white p-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              {companyLogo ? (
                <img src={companyLogo} alt="Company Logo" className="h-full w-full object-contain p-1" />
              ) : (
                <span className="text-sm font-black text-brand-700">A2B</span>
              )}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-base font-extrabold text-slate-950">{companyName}</h2>
              <p className="truncate text-xs font-medium text-slate-500">Secure finance workspace</p>
            </div>
          </div>
          <button
            className="rounded-xl border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-950 lg:hidden"
            onClick={toggleSidebar}
            aria-label="Close Sidebar"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="mx-4 mt-4 rounded-2xl border border-brand-100 bg-brand-50 px-3 py-3">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-xs font-bold uppercase tracking-wide text-brand-700">Logged in as</span>
          </div>
          <p className="mt-1 text-sm font-extrabold capitalize text-slate-950">{userRole || 'Loading...'}</p>
        </div>

        <nav className="flex h-full flex-col p-4">
          <ul className="flex-1 space-y-1.5">
            {menuGroups.map((group, index) => (
              <li key={group.title}>
                {group.singleItem ? (
                  <Link
                    to={group.path}
                    className={`flex items-center gap-3 rounded-xl px-4 py-2.5 transition-all duration-200 ${
                      location.pathname === group.path
                        ? 'bg-brand-600 font-semibold text-white shadow-lg shadow-brand-600/20'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                    }`}
                    onClick={handleLinkClick}
                  >
                    <span className={location.pathname === group.path ? 'text-white' : 'text-slate-500'}>{getIcon(group.icon)}</span>
                    <span className="font-semibold">{group.title}</span>
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={() => toggleDropdown(index)}
                      className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 transition-all duration-200 ${
                        activeDropdown === index
                          ? 'bg-slate-100 text-slate-950'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                      }`}
                    >
                      <span className="text-slate-500">{getIcon(group.icon)}</span>
                      <span className="min-w-0 flex-1 truncate text-left font-semibold">{group.title}</span>
                      {activeDropdown === index ? (
                        <FiChevronDown className="shrink-0 text-sm text-slate-500" />
                      ) : (
                        <FiChevronRight className="shrink-0 text-sm text-slate-400" />
                      )}
                    </button>
                    {activeDropdown === index && (
                      <ul className="ml-9 mt-1 space-y-1 border-l border-slate-200 pl-3">
                        {group.items.map((item) => (
                          <li key={item.path}>
                            <Link
                              to={item.path}
                              className={`block rounded-lg px-3 py-2 text-sm transition-all duration-200 ${
                                location.pathname === item.path
                                  ? 'bg-brand-50 font-bold text-brand-700'
                                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-950'
                              }`}
                              onClick={handleLinkClick}
                            >
                              {item.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>

          <div className="mt-6 border-t border-slate-200 pt-4">
            <p className="text-center text-xs text-slate-400">
              2026 Algo2BotTXN<br />
              Secure Transaction Platform
            </p>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
